/**
 * Staff Assignments Tab Component
 * Displays and manages staff assignments for a report (proposal)
 */

import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Button, Badge, LoadingSpinner, EmptyState } from '../UI';
import { useToastStore } from '../../stores/toast';
import { useWorkspace } from '../../hooks/useWorkspace';
import { downloadReportV1, downloadReportV2 } from '../../api/reports';
import { listEntities } from '../../api/entities';
import { getSelectionCorrections } from '../../api/feedbackApi';
import {
  buildAssignmentsSignature,
  buildCurrentAssignmentNames,
  buildCurrentAssignmentsForSuggestions,
  buildProposalContentSignature,
  mergeProposalFromReportContent,
  resolveProposalStaffMemberIds,
  type StaffMemberOption,
} from '../../utils/staffSuggestionContext';
import {
  User,
  UserPlus,
  X,
  Edit,
  Trash2,
  Loader2,
  Clock,
  Award,
  Building2,
  AlertTriangle,
  Search,
  ChevronDown,
  ChevronRight,
  Settings,
} from 'lucide-react';
import { formatStaffMatchSummary } from '../../utils/formatStaffMatchSummary';

interface StaffAssignment {
  id: string;
  staff_member_id: string;
  staff_name?: string;
  staff_designations?: string[];
  staff_department?: string;
  staff_email?: string;
  staff_expertise_areas?: string[];
  staff_location?: Record<string, unknown> | string;
  role?: string;
  assigned_hours_per_week?: number;
  assigned_at: string;
  assigned_by?: string;
  status: 'active' | 'completed' | 'cancelled';
  notes?: string;
  assignment_metadata?: Record<string, any>;
}

/** RFP-derived staff position (from template result staff_requirements) */
export interface StaffRequirementPosition {
  position_id: string;
  role_title: string;
  scoring_criteria?: string | string[];
  qualifications?: Record<string, unknown>;
}

/** Suggested assignment from POST /rfp/staff/suggest-assignments (enriched with staff details) */
export interface SuggestedAssignment {
  position_id: string;
  staff_member_id: string;
  match_score: number;
  multi_role?: boolean;
  role_title?: string;
  staff_name?: string;
  staff_designations?: string[];
  staff_department?: string;
  staff_email?: string;
  staff_expertise_areas?: string[];
  staff_location?: Record<string, unknown> | string;
  matched_rules?: Array<{
    rule_name?: string;
    passed?: boolean;
    score_contribution?: number;
    weight?: number;
    explanation?: string;
  }>;
  failed_required_rules?: string[];
  explanation?: string;
  /** True when this row reflects the proposal/slate assignee, not a fresh top matcher pick. */
  is_current_assignment?: boolean;
}

interface StaffAssignmentsTabProps {
  reportId: string;
  report?: any;
  /** RFP-derived positions (from report.meta?.template_result?.staff_requirements or report.metadata) */
  staffRequirements?: StaffRequirementPosition[];
  /** Full template result for suggest-assignments (or pass templateJobId to load by job_id) */
  templateResult?: Record<string, unknown>;
  /** Production template job ID; when set and templateResult absent, suggest-assignments can use job_id */
  templateJobId?: string;
  /** When true, hide assign / edit / remove controls (viewer role). */
  readOnly?: boolean;
}

export function StaffAssignmentsTab({
  reportId,
  report,
  staffRequirements: staffRequirementsProp,
  templateResult: templateResultProp,
  templateJobId,
  readOnly = false,
}: StaffAssignmentsTabProps) {
  const { addToast } = useToastStore();
  const { projectId } = useWorkspace();
  const queryClient = useQueryClient();
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState<StaffAssignment | null>(null);
  const [suggestedAssignments, setSuggestedAssignments] = useState<SuggestedAssignment[]>([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [confirmingPositionIds, setConfirmingPositionIds] = useState<Set<string>>(new Set());
  const lastSuggestionsSignatureRef = useRef('');

  React.useEffect(() => {
    lastSuggestionsSignatureRef.current = '';
    setSuggestedAssignments([]);
  }, [reportId]);

  // RFP-derived positions: from props or report.meta?.template_result (API returns meta)
  const templateResult = templateResultProp ?? report?.meta?.template_result ?? (report as any)?.metadata?.template_result;
  const staffRequirements: StaffRequirementPosition[] =
    staffRequirementsProp ?? (templateResult?.staff_requirements as StaffRequirementPosition[] | undefined) ?? [];

  const { data: v2Content } = useQuery({
    queryKey: ['report-v2-content', reportId],
    queryFn: () => downloadReportV2(reportId),
    enabled: !!reportId,
    staleTime: 60_000,
  });

  const { data: v1Content } = useQuery({
    queryKey: ['report-v1-content', reportId],
    queryFn: () => downloadReportV1(reportId),
    enabled: !!reportId,
    staleTime: 60_000,
  });

  const { data: correctionsData } = useQuery({
    queryKey: ['selection-corrections', reportId],
    queryFn: () => getSelectionCorrections(reportId),
    enabled: !!reportId,
    staleTime: 60_000,
  });

  const { data: staffMembersData } = useQuery({
    queryKey: ['staff-members', projectId],
    queryFn: async () => {
      if (!projectId) return { staff_members: [] as StaffMemberOption[] };
      const entities = await listEntities(projectId, 'staff_member', 1000);
      return {
        staff_members: entities
          .filter((e) => (e.data as Record<string, unknown>)?.name)
          .map((e) => ({
            id: e.id,
            name: String((e.data as Record<string, unknown>).name ?? 'Unknown'),
          })),
      };
    },
    enabled: !!projectId && staffRequirements.length > 0,
    staleTime: 120_000,
  });

  const staffMemberOptions = staffMembersData?.staff_members ?? [];

  const proposalByPosition = useMemo(
    () => mergeProposalFromReportContent(v2Content, v1Content, staffRequirements),
    [v2Content, v1Content, staffRequirements],
  );

  const proposalByPositionResolved = useMemo(
    () => resolveProposalStaffMemberIds(proposalByPosition, staffMemberOptions),
    [proposalByPosition, staffMemberOptions],
  );

  const { data: assignmentsData, isLoading, refetch } = useQuery({
    queryKey: ['report-staff-assignments', reportId],
    queryFn: async () => {
      const { getAPIClient } = await import('../../api/client');
      const api = getAPIClient();
      const response = await api.get(`/api/rfp/staff/report-assignments/${reportId}`);
      return response;
    },
    enabled: !!reportId,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
  });

  const confirmedByPosition = useMemo(() => {
    const map: Record<string, { staff_name?: string; staff_member_id?: string }> = {};
    const raw = report?.meta?.staff_assignments;
    if (raw && typeof raw === 'object') {
      for (const [pid, val] of Object.entries(raw as Record<string, unknown>)) {
        if (!val || typeof val !== 'object') continue;
        const a = val as Record<string, unknown>;
        map[pid] = {
          staff_name: String(a.staff_name ?? a.name ?? ''),
          staff_member_id: a.staff_member_id ? String(a.staff_member_id) : undefined,
        };
      }
    }
    for (const a of assignmentsData?.assignments ?? []) {
      const pid = a.position_id || a.assignment_metadata?.position_id;
      if (pid) {
        map[pid] = { staff_name: a.staff_name, staff_member_id: a.staff_member_id };
      }
    }
    return map;
  }, [report?.meta?.staff_assignments, assignmentsData?.assignments]);

  const currentAssignmentsForSuggestions = useMemo(
    () =>
      buildCurrentAssignmentsForSuggestions(
        staffRequirements,
        proposalByPositionResolved,
        confirmedByPosition,
      ),
    [staffRequirements, proposalByPositionResolved, confirmedByPosition],
  );

  const proposalContentSignature = useMemo(
    () => buildProposalContentSignature(staffRequirements, proposalByPositionResolved),
    [staffRequirements, proposalByPositionResolved],
  );

  const currentAssignmentsSignature = useMemo(
    () => buildAssignmentsSignature(currentAssignmentsForSuggestions),
    [currentAssignmentsForSuggestions],
  );

  const currentAssignmentNames = useMemo(
    () =>
      buildCurrentAssignmentNames(
        staffRequirements,
        proposalByPosition,
        proposalByPositionResolved,
      ),
    [staffRequirements, proposalByPosition, proposalByPositionResolved],
  );

  const rejectedStaffIds = useMemo(
    () => correctionsData?.selection_corrections?.rejected_staff_member_ids ?? [],
    [correctionsData],
  );

  const assignments: StaffAssignment[] = assignmentsData?.assignments || [];

  // Remove assignment mutation (track which one is removing for loader; show staff name once in toast)
  const [removingAssignmentId, setRemovingAssignmentId] = useState<string | null>(null);
  const removeMutation = useMutation({
    mutationFn: async ({ assignmentId }: { assignmentId: string; staffName?: string }) => {
      const { getAPIClient } = await import('../../api/client');
      const api = getAPIClient();
      try {
        await api.delete(`/api/rfp/staff/report-assignments/${assignmentId}`);
      } catch (error: any) {
        throw new Error(error.response?.data?.detail || 'Failed to remove assignment');
      }
    },
    onMutate: ({ assignmentId }) => setRemovingAssignmentId(assignmentId),
    onSuccess: (_data, { staffName }) => {
      queryClient.invalidateQueries({ queryKey: ['report-staff-assignments'] });
      queryClient.invalidateQueries({ queryKey: ['all-report-assignments'] });
      queryClient.invalidateQueries({ queryKey: ['staff-capacity'] });
      queryClient.invalidateQueries({ queryKey: ['staff-availability'] });
      const message = staffName
        ? `${staffName} removed from assignment`
        : 'Staff assignment removed';
      addToast({ message, type: 'success' });
      refetch();
    },
    onError: (error: any) => {
      addToast({
        message: error.response?.data?.detail || 'Failed to remove assignment',
        type: 'error',
      });
    },
    onSettled: () => setRemovingAssignmentId(null),
  });

  const handleRemove = (assignmentId: string, staffName?: string) => {
    if (confirm('Remove this staff assignment?')) {
      removeMutation.mutate({ assignmentId, staffName });
    }
  };

  // Assign single staff to report (from suggestion) – role is project-dependent text (e.g. role_title)
  const assignMutation = useMutation({
    mutationFn: async ({
      staff_member_id,
      role,
      position_id,
    }: { staff_member_id: string; role?: string; position_id?: string }) => {
      const { getAPIClient } = await import('../../api/client');
      const api = getAPIClient();
      await api.post('/api/rfp/staff/assign-to-report', {
        report_id: reportId,
        staff_member_id,
        role: role ?? undefined,
        position_id: position_id ?? undefined,
      });
    },
    onSuccess: (_data, { staff_member_id }) => {
      queryClient.invalidateQueries({ queryKey: ['report-staff-assignments'] });
      queryClient.invalidateQueries({ queryKey: ['all-report-assignments'] });
      queryClient.invalidateQueries({ queryKey: ['staff-capacity'] });
      queryClient.invalidateQueries({ queryKey: ['staff-availability'] });
      addToast({ message: 'Staff assigned to proposal', type: 'success' });
      refetch();
      setSuggestedAssignments((prev) => prev.filter((s) => s.staff_member_id !== staff_member_id));
    },
    onError: (error: any) => {
      addToast({
        message: error?.response?.data?.detail ?? 'Failed to assign staff',
        type: 'error',
      });
    },
  });

  const handleConfirmSuggestion = (suggestion: SuggestedAssignment) => {
    assignMutation.mutate({
      staff_member_id: suggestion.staff_member_id,
      role: suggestion.role_title,
      position_id: suggestion.position_id,
    });
    setConfirmingPositionIds((prev) => new Set(prev).add(suggestion.position_id));
  };

  const handleConfirmAll = async () => {
    const toAssign = suggestedAssignments.filter(
      (s) => !assignments.some((a: StaffAssignment) => a.staff_member_id === s.staff_member_id)
    );
    if (toAssign.length === 0) {
      addToast({ message: 'No new suggestions to assign', type: 'info' });
      return;
    }
    const { getAPIClient } = await import('../../api/client');
    const api = getAPIClient();
    const assignedKeys = new Set<string>();
    for (const s of toAssign) {
      try {
        await api.post('/api/rfp/staff/assign-to-report', {
          report_id: reportId,
          staff_member_id: s.staff_member_id,
          role: s.role_title ?? undefined,
          position_id: s.position_id,
        });
        assignedKeys.add(`${s.position_id}:${s.staff_member_id}`);
      } catch (_e) {
        // continue; toast below for partial failure
      }
    }
    if (assignedKeys.size > 0) {
      queryClient.invalidateQueries({ queryKey: ['report-staff-assignments'] });
      queryClient.invalidateQueries({ queryKey: ['all-report-assignments'] });
      queryClient.invalidateQueries({ queryKey: ['staff-capacity'] });
      queryClient.invalidateQueries({ queryKey: ['staff-availability'] });
      addToast({ message: `${assignedKeys.size} staff member(s) assigned to proposal`, type: 'success' });
      refetch();
      setSuggestedAssignments((prev) =>
        prev.filter((s) => !assignedKeys.has(`${s.position_id}:${s.staff_member_id}`))
      );
    }
    if (assignedKeys.size < toAssign.length) {
      addToast({ message: 'Some assignments could not be completed (e.g. already assigned)', type: 'warning' });
    }
  };

  const loadSuggestedAssignments = useCallback(async (options?: { silent?: boolean }) => {
    if (!projectId) {
      if (!options?.silent) {
        addToast({ message: 'Project context required', type: 'error' });
      }
      return;
    }
    setLoadingSuggestions(true);
    try {
      const { getAPIClient } = await import('../../api/client');
      const api = getAPIClient();
      const body: {
        project_id: string;
        report_id: string;
        template_result?: Record<string, unknown>;
        job_id?: string;
        allow_multi_role?: boolean;
        current_assignments?: Record<string, string>;
        current_assignment_names?: Record<string, string>;
        exclude_staff_member_ids?: string[];
      } = {
        project_id: projectId,
        report_id: reportId,
        allow_multi_role: false,
        current_assignments: currentAssignmentsForSuggestions,
        current_assignment_names: currentAssignmentNames,
        exclude_staff_member_ids: rejectedStaffIds,
      };
      if (templateResult) body.template_result = templateResult;
      if (templateJobId) body.job_id = templateJobId;
      const response = await api.post(
        '/api/rfp/staff/suggest-assignments',
        body
      ) as { data?: { suggested_assignments?: SuggestedAssignment[] }; suggested_assignments?: SuggestedAssignment[] };
      const list = response.data?.suggested_assignments ?? response.suggested_assignments ?? [];
      setSuggestedAssignments(list);
      if (!options?.silent) {
        addToast({ message: 'Suggested staff loaded', type: 'success' });
      }
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Failed to load suggestions';
      if (!options?.silent) {
        addToast({ message, type: 'error' });
      }
    } finally {
      setLoadingSuggestions(false);
    }
  }, [
    projectId,
    reportId,
    templateResult,
    templateJobId,
    currentAssignmentsForSuggestions,
    currentAssignmentNames,
    rejectedStaffIds,
    addToast,
  ]);

  React.useEffect(() => {
    if (!projectId || !templateResult || staffRequirements.length === 0) return;
    const signature = `${reportId}|${currentAssignmentsSignature}|${proposalContentSignature}|${rejectedStaffIds.join(',')}`;
    if (lastSuggestionsSignatureRef.current === signature) {
      return;
    }
    lastSuggestionsSignatureRef.current = signature;
    void loadSuggestedAssignments({ silent: true });
  }, [
    projectId,
    templateResult,
    staffRequirements.length,
    reportId,
    currentAssignmentsSignature,
    proposalContentSignature,
    rejectedStaffIds,
    loadSuggestedAssignments,
  ]);

  const suggestedByPositionId = suggestedAssignments.reduce<Record<string, SuggestedAssignment>>((acc, s) => {
    if (s.position_id) acc[s.position_id] = s;
    return acc;
  }, {});

  // Staff suggested for multiple roles (for notification only)
  const multiRoleStaffIds = React.useMemo(() => {
    const byStaff: Record<string, number> = {};
    for (const s of suggestedAssignments) {
      const id = s.staff_member_id;
      if (id) byStaff[id] = (byStaff[id] ?? 0) + 1;
    }
    return new Set(Object.keys(byStaff).filter((id) => byStaff[id] > 1));
  }, [suggestedAssignments]);
  const multiRoleNames = React.useMemo(() => {
    return suggestedAssignments
      .filter((s) => s.staff_member_id && multiRoleStaffIds.has(s.staff_member_id))
      .map((s) => s.staff_name || s.staff_member_id.slice(0, 8))
      .filter((n, i, arr) => arr.indexOf(n) === i);
  }, [suggestedAssignments, multiRoleStaffIds]);

  // Dynamically import StaffAssignmentModal from plugin
  const [StaffAssignmentModal, setStaffAssignmentModal] = useState<React.ComponentType<any> | null>(null);
  // KBF-065: Dynamically import StaffSearchAndMatchPanel for "Search & match staff" in-tab
  const [StaffSearchAndMatchPanel, setStaffSearchAndMatchPanel] = useState<React.ComponentType<any> | null>(null);
  const [showSearchMatchPanel, setShowSearchMatchPanel] = useState(false);
  // Full Staff Management page (list + dedup) – same as Proposal Management "Staff management" sub-tab
  const [StaffManagementPageComponent, setStaffManagementPageComponent] = useState<React.ComponentType<any> | null>(null);
  const [showStaffManagementPanel, setShowStaffManagementPanel] = useState(false);
  const [staffManagementLoadError, setStaffManagementLoadError] = useState(false);
  const [staffManagementLoading, setStaffManagementLoading] = useState(false);

  React.useEffect(() => {
    const loadModal = async () => {
      try {
        // Try relative path first (works during build)
        try {
          // @ts-ignore - Plugin path may not be available at compile time
          const module = await import('../../../../plugins/rfp-agent/src/components/StaffAssignmentModal');
          if (module && module.StaffAssignmentModal) {
            setStaffAssignmentModal(() => module.StaffAssignmentModal);
            return;
          }
        } catch (e) {
          console.debug('StaffAssignmentModal import failed (relative path):', e);
        }
        
        // Fallback: try alias path (only works at runtime when alias is configured)
        try {
          // Construct path dynamically to prevent Rollup from analyzing it
          // @ts-ignore - Dynamic import path
          const pluginPath = '@tracekit' + '/plugin-rfp-agent/components/StaffAssignmentModal';
          const module = await import(/* @vite-ignore */ pluginPath);
          if (module && module.StaffAssignmentModal) {
            setStaffAssignmentModal(() => module.StaffAssignmentModal);
            return;
          }
        } catch (e) {
          console.debug('StaffAssignmentModal import failed (alias path):', e);
        }
      } catch (error) {
        console.warn('StaffAssignmentModal not available:', error);
      }
    };
    
    loadModal();
  }, []);

  // KBF-065: Load StaffSearchAndMatchPanel when user expands "Search & match staff"
  React.useEffect(() => {
    if (!showSearchMatchPanel) return;
    const loadPanel = async () => {
      try {
        try {
          // @ts-ignore - Plugin path may not be available at compile time
          const module = await import('../../../../plugins/rfp-agent/src/components/StaffSearchAndMatchPanel');
          if (module?.StaffSearchAndMatchPanel) {
            setStaffSearchAndMatchPanel(() => module.StaffSearchAndMatchPanel);
            return;
          }
        } catch (e) {
          console.debug('StaffSearchAndMatchPanel import failed (relative path):', e);
        }
        try {
          // @ts-ignore - Dynamic import path
          const pluginPath = '@tracekit' + '/plugin-rfp-agent/components/StaffSearchAndMatchPanel';
          const module = await import(/* @vite-ignore */ pluginPath);
          if (module?.StaffSearchAndMatchPanel) {
            setStaffSearchAndMatchPanel(() => module.StaffSearchAndMatchPanel);
          }
        } catch (e) {
          console.debug('StaffSearchAndMatchPanel import failed (alias path):', e);
        }
      } catch (error) {
        console.warn('StaffSearchAndMatchPanel not available:', error);
      }
    };
    loadPanel();
  }, [showSearchMatchPanel]);

  // Load StaffManagementPage when user expands "Staff management"
  React.useEffect(() => {
    if (!showStaffManagementPanel || StaffManagementPageComponent) return;
    setStaffManagementLoading(true);
    setStaffManagementLoadError(false);
    const loadPage = async () => {
      // Attempt 1: subdirectory path (preferred — contains dedup buttons)
      try {
        // @ts-ignore - Plugin path may not be available at compile time
        const module = await import('../../../../plugins/rfp-agent/src/pages/staff-management/StaffManagementPage');
        if (module?.StaffManagementPage) {
          setStaffManagementPageComponent(() => module.StaffManagementPage);
          setStaffManagementLoading(false);
          return;
        }
      } catch (e) {
        console.debug('StaffManagementPage import failed (subdirectory path):', e);
      }
      // Attempt 2: root pages path
      try {
        // @ts-ignore - Plugin path may not be available at compile time
        const module = await import('../../../../plugins/rfp-agent/src/pages/StaffManagementPage');
        if (module?.StaffManagementPage) {
          setStaffManagementPageComponent(() => module.StaffManagementPage);
          setStaffManagementLoading(false);
          return;
        }
      } catch (e) {
        console.debug('StaffManagementPage import failed (root path):', e);
      }
      // Attempt 3: alias path (runtime only)
      try {
        // @ts-ignore - Dynamic import path
        const pluginPath = '@tracekit' + '/plugin-rfp-agent/pages/staff-management/StaffManagementPage';
        const module = await import(/* @vite-ignore */ pluginPath);
        if (module?.StaffManagementPage) {
          setStaffManagementPageComponent(() => module.StaffManagementPage);
          setStaffManagementLoading(false);
          return;
        }
      } catch (e) {
        console.debug('StaffManagementPage import failed (alias path):', e);
      }
      console.warn('StaffManagementPage: all import paths failed');
      setStaffManagementLoadError(true);
      setStaffManagementLoading(false);
    };
    loadPage();
  }, [showStaffManagementPanel, StaffManagementPageComponent]);

  if (isLoading) {
    return (
      <Card className="p-8">
        <div className="flex items-center justify-center">
          <LoadingSpinner />
          <span className="ml-3 text-gray-600">Loading staff assignments...</span>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <User className="w-5 h-5" />
            Staff Assignments
          </h3>
          <p className="text-sm text-theme-text-secondary mt-1">
            {assignments.length} staff member{assignments.length !== 1 ? 's' : ''} assigned to this proposal
          </p>
        </div>
        {!readOnly && StaffAssignmentModal && (
          <Button
            variant="primary"
            onClick={() => setShowAssignModal(true)}
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Assign Staff
          </Button>
        )}
      </div>

      {/* Multi-role warning: staff suggested for more than one role */}
      {multiRoleNames.length > 0 && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 ">
          <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
          <div>
            <span className="font-medium">Same person suggested for multiple roles (for your reference): </span>
            <span>{multiRoleNames.join(', ')}</span>
          </div>
        </div>
      )}

      {/* Confirmed staff assigned to this proposal (at top) */}
      {assignments.length === 0 ? (
        <Card className="p-8">
          <EmptyState
            icon={<User className="w-12 h-12" />}
            title="No Staff Assigned"
            description="Assign staff members to this proposal to track workload and capacity"
          />
        </Card>
      ) : (
        <div className="space-y-3">
          {assignments.map((assignment: StaffAssignment) => (
            <Card key={assignment.id} className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="font-semibold text-theme-text">
                      {assignment.staff_name || 'Unknown Staff'}
                    </span>
                    {assignment.role && (
                      <Badge variant="default" className="text-xs">
                        {assignment.role}
                      </Badge>
                    )}
                    <Badge
                      variant={assignment.status === 'active' ? 'default' : 'info'}
                      className="text-xs"
                    >
                      {assignment.status}
                    </Badge>
                  </div>
                  
                  <div className="flex flex-wrap gap-4 text-sm text-theme-text-secondary">
                    {assignment.staff_department && (
                      <span className="flex items-center gap-1">
                        <Building2 className="w-3 h-3" />
                        {assignment.staff_department}
                      </span>
                    )}
                    {assignment.staff_designations && assignment.staff_designations.length > 0 && (
                      <span className="flex items-center gap-1">
                        <Award className="w-3 h-3" />
                        {Array.isArray(assignment.staff_designations) ? assignment.staff_designations.join(', ') : String(assignment.staff_designations)}
                      </span>
                    )}
                    {assignment.staff_email && (
                      <span>{assignment.staff_email}</span>
                    )}
                    {assignment.staff_expertise_areas && assignment.staff_expertise_areas.length > 0 && (
                      <span>Expertise: {Array.isArray(assignment.staff_expertise_areas) ? assignment.staff_expertise_areas.slice(0, 5).join(', ') : String(assignment.staff_expertise_areas)}</span>
                    )}
                    {assignment.staff_location && (
                      <span>
                        {typeof assignment.staff_location === 'string'
                          ? assignment.staff_location
                          : [assignment.staff_location?.office, assignment.staff_location?.city, assignment.staff_location?.province].filter(Boolean).join(', ')}
                      </span>
                    )}
                    {assignment.assigned_hours_per_week != null && assignment.assigned_hours_per_week > 0 && (
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {assignment.assigned_hours_per_week}h/week
                      </span>
                    )}
                    {assignment.assigned_at && (
                      <span className="text-xs">
                        Assigned: {new Date(assignment.assigned_at).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                  
                  {assignment.notes && (
                    <div className="mt-2 text-sm text-theme-text">
                      <span className="font-medium">Notes: </span>
                      {assignment.notes}
                    </div>
                  )}
                </div>
                
                {!readOnly && (
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleRemove(assignment.id, assignment.staff_name)}
                    disabled={removingAssignmentId !== null}
                  >
                    {removingAssignmentId === assignment.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </Button>
                </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Positions from RFP (when template result has staff_requirements) */}
      {staffRequirements.length > 0 && (
        <Card className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold text-theme-text">
              Positions from RFP
            </h4>
            <div className="flex items-center gap-2">
              {!readOnly && suggestedAssignments.length > 0 && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleConfirmAll}
                  disabled={
                    assignMutation.isPending ||
                    suggestedAssignments.filter((s) => !assignments.some((a: StaffAssignment) => a.staff_member_id === s.staff_member_id)).length === 0
                  }
                >
                  Confirm all
                </Button>
              )}
              {!readOnly && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => loadSuggestedAssignments()}
                disabled={loadingSuggestions || !projectId}
              >
                {loadingSuggestions ? (
                  <>
                    <LoadingSpinner className="w-4 h-4 mr-2 inline" />
                    Loading…
                  </>
                ) : (
                  'Load suggested staff'
                )}
              </Button>
              )}
            </div>
          </div>
          <ul className="space-y-3">
            {staffRequirements.map((pos) => {
              const suggested = suggestedByPositionId[pos.position_id];
              const matchSummary = suggested
                ? formatStaffMatchSummary(suggested, 180)
                : '';
              const alreadyAssigned = suggested && assignments.some((a: StaffAssignment) => a.staff_member_id === suggested.staff_member_id);
              const displayName = suggested ? (suggested.staff_name ?? suggested.staff_member_id) : null;
              const proposalName = proposalByPosition[pos.position_id]?.name;
              const suggestionLabel = suggested?.is_current_assignment ? 'In proposal' : 'Suggested';
              const rawScore = suggested != null && typeof suggested.match_score === 'number' ? suggested.match_score : suggested != null ? Number(suggested.match_score) : NaN;
              const matchPct = !Number.isNaN(rawScore) ? (rawScore <= 1 ? rawScore * 100 : rawScore) : null;
              return (
                <li
                  key={pos.position_id}
                  className="flex flex-col gap-2 rounded border border-theme-border p-3 text-sm last:border-b"
                >
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="min-w-0">
                      <span className="font-medium text-theme-text">{pos.role_title}</span>
                      {proposalName && (
                        <span className="ml-2 text-theme-text-secondary">
                          · In proposal: <strong>{proposalName.split(',')[0]?.trim() ?? proposalName}</strong>
                        </span>
                      )}
                      {displayName != null && (
                        <span className="ml-2 text-theme-text-secondary">
                          · {suggestionLabel}: <strong>{displayName}</strong>
                          {matchPct != null && !Number.isNaN(matchPct) && (
                            <span className="text-theme-text-muted"> ({matchPct.toFixed(0)}% match)</span>
                          )}
                        </span>
                      )}
                      {matchSummary && (
                        <div className="mt-1 text-theme-text-muted text-xs" title={matchSummary}>
                          Match: {matchSummary}
                        </div>
                      )}
                    </div>
                    {suggested && !alreadyAssigned && !readOnly && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleConfirmSuggestion(suggested)}
                        disabled={assignMutation.isPending || confirmingPositionIds.has(suggested.position_id)}
                      >
                        {confirmingPositionIds.has(suggested.position_id) || (assignMutation.isPending && assignMutation.variables?.staff_member_id === suggested.staff_member_id) ? (
                          <LoadingSpinner className="w-4 h-4" />
                        ) : (
                          'Confirm'
                        )}
                      </Button>
                    )}
                  </div>
                  {suggested && (
                    <div className="rounded bg-theme-elevated p-3 space-y-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-theme-text">
                          {suggested.staff_name ?? suggested.staff_member_id}
                        </span>
                        <Badge variant="default" className="text-xs">
                          {(matchPct != null ? matchPct : 0).toFixed(0)}% match
                        </Badge>
                        {suggested.multi_role && (
                          <Badge variant="info" className="text-xs">Multiple roles</Badge>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-theme-text-secondary">
                        {suggested.staff_department && (
                          <span className="flex items-center gap-1">
                            <Building2 className="w-3 h-3" />
                            {suggested.staff_department}
                          </span>
                        )}
                        {suggested.staff_designations && suggested.staff_designations.length > 0 && (
                          <span className="flex items-center gap-1">
                            <Award className="w-3 h-3" />
                            {Array.isArray(suggested.staff_designations) ? suggested.staff_designations.join(', ') : String(suggested.staff_designations)}
                          </span>
                        )}
                        {suggested.staff_email && (
                          <span>{suggested.staff_email}</span>
                        )}
                        {suggested.staff_expertise_areas && suggested.staff_expertise_areas.length > 0 && (
                          <span>Expertise: {Array.isArray(suggested.staff_expertise_areas) ? suggested.staff_expertise_areas.slice(0, 3).join(', ') : String(suggested.staff_expertise_areas)}</span>
                        )}
                        {suggested.staff_location && (
                          <span>
                            {typeof suggested.staff_location === 'string'
                              ? suggested.staff_location
                              : [(suggested.staff_location as Record<string, unknown>)?.office, (suggested.staff_location as Record<string, unknown>)?.city, (suggested.staff_location as Record<string, unknown>)?.province].filter(Boolean).join(', ')}
                          </span>
                        )}
                      </div>
                      {alreadyAssigned && (
                        <span className="text-xs text-green-600 dark:text-green-400">Already assigned to this proposal</span>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </Card>
      )}

      {/* KBF-065: Search & match staff – integrated into Staff Assignments tab */}
      <Card className="p-0 overflow-hidden">
        <button
          type="button"
          onClick={() => setShowSearchMatchPanel(!showSearchMatchPanel)}
          className="w-full flex items-center justify-between gap-2 p-4 text-left hover:bg-theme-surface-hover transition-colors"
        >
          <span className="flex items-center gap-2 font-medium text-theme-text">
            <Search className="w-5 h-5 text-gray-500" />
            Search & match staff
          </span>
          {showSearchMatchPanel ? (
            <ChevronDown className="w-5 h-5 text-gray-500" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-500" />
          )}
        </button>
        {showSearchMatchPanel && StaffSearchAndMatchPanel && (
          <div className="border-t border-gray-200 dark:border-gray-700 p-4 bg-theme-elevated">
            <StaffSearchAndMatchPanel
              reportId={reportId}
              onAssignSuccess={() => {
                refetch();
                queryClient.invalidateQueries({ queryKey: ['report-staff-assignments'] });
                queryClient.invalidateQueries({ queryKey: ['all-report-assignments'] });
              }}
            />
          </div>
        )}
      </Card>

      {/* Staff management – full page (list + dedup) same as Proposal Management "Staff management" sub-tab */}
      <Card className="p-0 overflow-hidden">
        <button
          type="button"
          onClick={() => setShowStaffManagementPanel(!showStaffManagementPanel)}
          className="w-full flex items-center justify-between gap-2 p-4 text-left hover:bg-theme-surface-hover transition-colors"
        >
          <span className="flex items-center gap-2 font-medium text-theme-text">
            <Settings className="w-5 h-5 text-gray-500" />
            Staff management
          </span>
          {showStaffManagementPanel ? (
            <ChevronDown className="w-5 h-5 text-gray-500" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-500" />
          )}
        </button>
        {showStaffManagementPanel && (
          <div className="border-t border-gray-200 dark:border-gray-700 p-4 bg-theme-elevated">
            {staffManagementLoading && (
              <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Loading staff management...</span>
              </div>
            )}
            {staffManagementLoadError && !staffManagementLoading && (
              <div className="flex items-center gap-2 text-sm text-amber-600 py-2">
                <AlertTriangle className="w-4 h-4" />
                <span>Staff management panel could not be loaded. Please refresh the page and try again.</span>
              </div>
            )}
            {StaffManagementPageComponent && !staffManagementLoading && (
              <StaffManagementPageComponent />
            )}
          </div>
        )}
      </Card>

      {/* Assign Staff Modal */}
      {StaffAssignmentModal && showAssignModal && (
        <StaffAssignmentModal
          reportId={reportId}
          reportName={report?.name || report?.client_name}
          projectId={projectId || ''}
          isOpen={showAssignModal}
          onClose={() => setShowAssignModal(false)}
          onSuccess={() => {
            refetch();
            // Also invalidate other assignment queries to refresh counts in other tabs
            queryClient.invalidateQueries({ queryKey: ['all-report-assignments'] });
            queryClient.invalidateQueries({ queryKey: ['report-staff-assignments'] });
            queryClient.invalidateQueries({ queryKey: ['staff-capacity'] });
            queryClient.invalidateQueries({ queryKey: ['staff-availability'] });
          }}
        />
      )}
    </div>
  );
}
